<template>
  <div>
    <div v-if="loading" style="background-color: #F4F4F5;">
      <div class="px-4 py-2 flex items-center gap-3 border-b border-gray-100">
        <div class="h-6 w-20 bg-gray-200 rounded animate-pulse"></div>
        <div class="h-3 w-16 bg-gray-100 rounded animate-pulse"></div>
      </div>
      <div class="p-3 space-y-3">
        <div v-for="n in 3" :key="'sk-' + n" class="bg-white rounded-lg px-4 py-3" style="border: 1px solid #E8E9E9;">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <div class="h-4 w-14 bg-gray-200 rounded animate-pulse"></div>
              <div class="h-3.5 w-12 bg-gray-200 rounded animate-pulse"></div>
              <div class="h-3 w-32 bg-gray-100 rounded animate-pulse"></div>
            </div>
            <div class="h-4 w-16 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div class="flex items-center gap-3 mt-1.5">
            <div class="h-3 w-20 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 w-16 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div class="mt-2.5 space-y-2">
            <div class="rounded-md px-3 py-2" style="background-color: #F9FAFB; border: 1px solid #E8E9E9;">
              <div class="flex items-center gap-2">
                <div class="h-3 w-14 bg-gray-200 rounded animate-pulse"></div>
                <div class="h-3 w-28 bg-gray-100 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="opportunities.length === 0" class="text-center py-8 text-gray-500 text-xs">
      No coding opportunities found.
    </div>
    <div v-else style="background-color: #F4F4F5;">
      <!-- Filter -->
      <div class="px-4 py-2 flex items-center gap-3 border-b border-gray-100">
        <select v-model="statusFilter" class="px-2 py-1 text-xs border border-gray-200 rounded-md bg-white" style="color: #353537;">
          <option value="active">Active</option>
          <option value="all">All</option>
          <option value="completed">Completed</option>
          <option value="expired">Expired</option>
        </select>
        <span class="text-[10px]" style="color: #A5A5A6;">
          {{ filteredOpps.length }} of {{ opportunities.length }}
        </span>
      </div>

      <!-- Opportunity cards -->
      <div class="p-3 space-y-3">
        <div
          v-for="opp in filteredOpps"
          :key="opp.id"
          class="bg-white rounded-lg px-4 py-3"
          style="border: 1px solid #E8E9E9;"
        >
          <!-- Header: type badge + HCC + status -->
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span :class="typeBadge(opp.type)">{{ opp.type }}</span>
              <span class="text-xs font-semibold" style="color: #353537;">{{ opp.hcc_code }}</span>
              <span class="text-[11px]" style="color: #626264;">{{ opp.description }}</span>
            </div>
            <div class="flex items-center gap-1.5">
              <span v-if="opp.opp_status === 'Completed'" class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-50 text-green-700">Completed</span>
              <span v-else-if="opp.opp_status === 'Expired'" class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-500">Expired</span>
              <span v-if="opp.current_tier" :class="tierBadge(opp.current_tier)">{{ opp.current_tier }}</span>
              <button
                v-if="opp.task_id"
                @click.stop="openOpportunity(opp)"
                class="text-[10px] cursor-pointer hover:underline"
                style="color: #7799C2;"
              >Open ↗</button>
            </div>
          </div>

          <!-- Row 2: Last coder + date + priority + claim date -->
          <div class="flex items-center gap-3 mt-1.5 text-[11px]">
            <template v-if="opp.last_coder">
              <span style="color: #353537; font-weight: 600;">{{ opp.last_coder }}</span>
              <span style="color: #626264;">{{ opp.last_touched }}</span>
            </template>
            <span v-if="opp.priority_score" style="color: #626264;">
              Priority: {{ Math.round(opp.priority_score).toLocaleString() }}
            </span>
            <span v-if="opp.claim_date" style="color: #9F4D30; font-weight: 500;">
              Claim: {{ opp.claim_date }}
            </span>
          </div>

          <!-- Code Suggestions -->
          <div v-if="opp.work_items?.length" class="mt-2.5 space-y-2">
            <div
              v-for="(w, idx) in opp.work_items"
              :key="idx"
              class="rounded-md px-3 py-2"
              style="background-color: #F9FAFB; border: 1px solid #E8E9E9;"
            >
              <!-- ICD code + description + status -->
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="text-[11px] font-mono font-semibold" style="color: #353537;">{{ w.icd_code }}</span>
                  <span class="text-[10px]" style="color: #626264;">{{ w.short_description }}</span>
                </div>
                <div class="flex items-center gap-1.5">
                  <span v-if="w.status" class="text-[10px] px-1.5 py-0.5 rounded font-medium"
                    :class="{
                      'bg-[#EBF4FF] text-[#4C6E97]': w.status?.includes('Suspect'),
                      'bg-[#F1F9F0] text-[#88A484]': w.status?.includes('Recapture'),
                    }"
                  >{{ w.status }}</span>
                  <span v-if="w.has_images" class="text-[10px]" style="color: #A5A5A6;">
                    <FontAwesomeIcon :icon="['fas', 'image']" />
                  </span>
                </div>
              </div>

              <!-- Explanation -->
              <div v-if="w.explanation" class="mt-1 text-[10px] leading-relaxed" style="color: #626264;">
                "{{ w.explanation }}"
              </div>

              <!-- QA outcome -->
              <div v-if="w.qa_concurrence !== null && w.qa_concurrence !== undefined" class="mt-1.5 text-[10px]">
                <span v-if="w.qa_concurrence === true" class="font-medium text-green-600">
                  <FontAwesomeIcon :icon="['fas', 'check']" class="mr-0.5" /> QA Accepted
                </span>
                <span v-else class="font-medium text-red-600">
                  <FontAwesomeIcon :icon="['fas', 'xmark']" class="mr-0.5" /> QA Rejected
                </span>
                <span v-if="w.qa_reviewer" class="ml-1.5" style="color: #A5A5A6;">by {{ w.qa_reviewer }}</span>
                <div v-if="w.qa_disagree_reason" class="mt-0.5 pl-3 text-red-600" style="font-style: italic;">
                  {{ w.qa_disagree_reason }}
                </div>
              </div>

              <!-- Coder + date -->
              <div class="mt-1 text-[10px]" style="color: #626264;">
                {{ w.coder }} · {{ w.created_at }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import abortable from '@/mixins/abortable';

export default {
  name: 'Patient_Coding_Activity',
  components: { FontAwesomeIcon },
  mixins: [abortable],
  props: {
    patientIdentifier: { type: String, required: true },
    entityId: { type: Number, default: null },
  },
  emits: ['open-task'],
  data() {
    return {
      opportunities: [],
      loading: false,
      statusFilter: 'active',
    };
  },
  computed: {
    filteredOpps() {
      let opps = this.opportunities;
      if (this.statusFilter !== 'all') {
        const status = this.statusFilter.charAt(0).toUpperCase() + this.statusFilter.slice(1);
        opps = opps.filter(o => o.opp_status === status);
      }
      // Sort by last_touched date descending
      return [...opps].sort((a, b) => {
        const da = a.last_touched || '';
        const db = b.last_touched || '';
        return db.localeCompare(da);
      });
    },
  },
  watch: {
    patientIdentifier: {
      immediate: true,
      handler() {
        const signal = this._renewAbortSignal();
        this.loadData(signal);
      },
    },
  },
  methods: {
    async loadData(signal) {
      if (!this.patientIdentifier) return;
      this.loading = true;
      try {
        const response = await axios.get(
          `/api/coding/${this.patientIdentifier}/opportunities/`,
          { signal },
        );
        this.opportunities = response.data.opportunities || [];
      } catch (error) {
        if (this._isAborted(error)) return;
        console.error('Failed to load opportunities:', error);
      } finally {
        if (!signal?.aborted) this.loading = false;
      }
    },
    openOpportunity(opp) {
      if (!opp.task_id) return;
      this.$emit('open-task', {
        task_id: opp.task_id,
        type: opp.type,
        hcc_code: opp.hcc_code,
        description: opp.description,
        current_tier: opp.current_tier,
      });
    },
    typeBadge(type) {
      const base = 'inline-flex px-2 py-0.5 text-[11px] font-medium rounded';
      switch (type) {
        case 'Suspect': return `${base} bg-[#EBF4FF] text-[#4C6E97]`;
        case 'Recapture': return `${base} bg-[#F1F9F0] text-[#88A484]`;
        case 'Resubmissions': return `${base} bg-[#FFF7F4] text-[#9F4D30]`;
        default: return `${base} bg-gray-100 text-gray-700`;
      }
    },
    tierBadge(tier) {
      const base = 'inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded';
      switch (tier) {
        case '1LR': return `${base} bg-blue-50 text-blue-600`;
        case '2LR': return `${base} bg-purple-50 text-purple-600`;
        case '3LR': return `${base} bg-indigo-50 text-indigo-600`;
        case 'Confirm': return `${base} bg-amber-50 text-amber-700`;
        default: return `${base} bg-gray-100 text-gray-500`;
      }
    },
  },
};
</script>
